import stringify from 'fast-safe-stringify';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subject } from 'rxjs';

import { Injectable, OnDestroy } from '@angular/core';
import { UrlHelperService } from '@core/services/url-helper.service';
import { environment } from '@environments/environment';
import { BreakpointService } from '@libs/breakpoints';

/**
 * Provides "safe" usage of browser storage API with options for expiration and shared sessionStorage between tabs as well as a fallback to using cookies.
 */
@Injectable({
    providedIn: 'root'
})
export class StorageService implements OnDestroy {
    private readonly eventRequestKey: string = 'KK_SHARED_SESSION_REQUEST';
    private readonly eventResponseKey: string = 'KK_SHARED_SESSION_RESPONSE';
    private readonly eventClearKey: string = 'KK_SHARED_SESSION_CLEAR';
    private readonly eventRemoveKey: string = 'KK_SHARED_KEY_REMOVE';
    private readonly externalChanges$: Subject<StorageChange> = new Subject();
    private readonly cookieFallbackPrefix: string = 'fallback-';
    private readonly cookieDomain: string = '.' + this.urlHelper.getRootDomain();
    private readonly useSecureCookies: boolean = environment.production;

    /**
     * Is session- and localStorage supported and enabled.
     * NOTE: You don't need to check support before using this API. Support is checked internally to avoid errors.
     */
    public readonly isStorageSupported: boolean = false;

    /**
     * True if a cookie-based fallback is possible. Fallback only happens if the options object passed has this enabled as well.
     */
    public readonly isCookieSupported: boolean = false;

    constructor(
        private breakpoint: BreakpointService,
        private cookie: CookieService,
        private urlHelper: UrlHelperService
    ) {
        if (this.breakpoint.isBrowser()) {
            this.isCookieSupported = true;
            if ((typeof localStorage as any) !== 'undefined' && (typeof sessionStorage as any) !== 'undefined') {
                const testKey = 'key-test-qwertyuioplkjhgfdsasxcvbnm';
                const testVal = 'value';
                try {
                    localStorage.setItem(testKey, testVal);
                    localStorage.removeItem(testKey);
                    sessionStorage.setItem(testKey, testVal);
                    sessionStorage.removeItem(testKey);
                    this.isStorageSupported = true;
                } catch {
                    this.isStorageSupported = false;
                }
            }

            if (this.isStorageSupported) {
                // Setup listener to handle requests and responses from other tabs.
                window.addEventListener('storage', this.storageEventHandler.bind(this));

                // Request shared session from other tabs.
                this.requestSharedSession();
            }
        }
    }

    public ngOnDestroy(): void {
        this.externalChanges$.complete();
        if (this.isStorageSupported) {
            window.removeEventListener('storage', this.storageEventHandler);
        }
    }

    /**
     * Subscribe to this to get notified when other tabs have caused modification of "shared" sessionStorage.
     * If the value of an entry is "undefined" it means it was removed completely.
     */
    public getExternalChanges(): Observable<StorageChange> {
        return this.externalChanges$.asObservable();
    }

    /**
     * Store an object in session- or localStorage safely. If storage is not supported it will do nothing.
     * @param key - Key used to store the value.
     * @param value - The value to store. If value is undefined/null/'' it won't be stored.
     * @param config - Configure accessibility and expiration of value.
     */
    public set<T = unknown>(key: string, value: T, config: StorageObjectConfig = {}): void {
        const storageObject: StorageObject = {
            value: value !== undefined ? value : null
        };
        if (config.hoursToLive) {
            const ttlDate: Date = new Date();
            storageObject.timestamp = ttlDate.setHours(ttlDate.getHours() + config.hoursToLive);
        }
        if (config.sharedSession) {
            storageObject.sharedSession = config.sharedSession;
        }

        if (this.isStorageSupported && !config.useCookie) {
            this.setStorage(key, storageObject, config);
        } else if (this.isCookieSupported && (config.useCookie || config.cookieFallback)) {
            this.setCookie(key, storageObject, config);
        }
    }

    /**
     * Get an object from storage.
     * @param key - Key used to store the value.
     */
    public get<T = any>(key: string): T | undefined {
        let stored;
        if (this.isStorageSupported) {
            stored = sessionStorage.getItem(key) || localStorage.getItem(key);
        } else if (this.isCookieSupported) {
            stored = this.cookie.get(key) || this.cookie.get(this.cookieFallbackPrefix + key);
        }

        if (stored) {
            try {
                const storageObject: StorageObject = JSON.parse(stored);
                if (storageObject.timestamp && storageObject.timestamp < Date.now()) {
                    this.remove(key);
                    return undefined;
                }
                if (storageObject.value !== undefined) {
                    return storageObject.value;
                }
                return storageObject as any;
            } catch {}
        }
        return undefined;
    }

    /**
     * Remove specific object.
     * @param key - Key used to store the value.
     */
    public remove(key: string): void {
        if (this.isStorageSupported) {
            const session: StorageObject = JSON.parse(sessionStorage.getItem(key) || '{}');
            if (session && session.sharedSession) {
                this.dispatchRemoveKey(key);
            }
            sessionStorage.removeItem(key);
            localStorage.removeItem(key);
        } else if (this.isCookieSupported) {
            this.cookie.delete(key, '/', this.cookieDomain);
            this.cookie.delete(this.cookieFallbackPrefix + key, '/', this.cookieDomain);
        }
    }

    /**
     * Clears entire storage.
     * @param session - If true sessionStorage will be cleared (default is true).
     * @param local - If true localStorage will be cleared (default is true).
     * @param deleteAllCookies - If true ALL cookies will also be deleted (NOT RECOMMENDED).
     */
    public clear(session: boolean = true, local: boolean = true, deleteAllCookies: boolean = false): void {
        if (this.isStorageSupported) {
            if (session) {
                this.dispatchClearSession();
                sessionStorage.clear();
            }
            if (local) {
                localStorage.clear();
            }
        } else if (this.isCookieSupported) {
            if (deleteAllCookies) {
                this.cookie.deleteAll('/', this.cookieDomain);
            } else {
                Object.keys(this.cookie.getAll())
                    .filter(key => key.indexOf(this.cookieFallbackPrefix) > -1)
                    .forEach(key => this.cookie.delete(key, '/', this.cookieDomain));
            }
        }
    }

    /**
     * Sets data using storage API.
     * @param key
     * @param storageObject
     * @param config
     */
    private setStorage(key: string, storageObject: StorageObject, config: StorageObjectConfig) {
        const newData = stringify(storageObject);
        if (config.sessionOnly) {
            const existingData = sessionStorage.getItem(key);
            sessionStorage.setItem(key, newData);
            if (storageObject.sharedSession && newData !== existingData) {
                this.dispatchSharedSession();
            }
        } else {
            localStorage.setItem(key, newData);
        }
    }

    /**
     * Sets data using cookie (ngx-cookie-service).
     * @param key
     * @param storageObject
     * @param config
     */
    private setCookie(key: string, storageObject: StorageObject, config: StorageObjectConfig) {
        const cookieData = stringify(storageObject);
        const cookieKey = (config.cookieFallback ? this.cookieFallbackPrefix : '') + key;
        // Set expires to undefined for session cookies.
        let cookieExpires: Date | undefined;
        if (storageObject.timestamp) {
            // Use specified timestamp as basis for expiration.
            cookieExpires = new Date(storageObject.timestamp);
        } else if (!config.sessionOnly) {
            // Default to one year if no other specification exists.
            const now = new Date();
            cookieExpires = new Date(now.setDate(now.getDate() + 365));
        }
        this.cookie.set(cookieKey, cookieData, cookieExpires, '/', this.cookieDomain, this.useSecureCookies, 'Lax');
    }

    /**
     * Sets and removes dummy-data with the "request key" in localStorage to trigger the "storage" event in other tabs.
     * This allows existing tabs to dispatch shared session data to newly opened tabs.
     */
    private requestSharedSession(): void {
        localStorage.setItem(this.eventRequestKey, Date.now().toString());
        localStorage.removeItem(this.eventRequestKey);
    }

    /**
     * Sets and removes shared session data with the "response key" in localStorage.
     * This triggers the "storage" event in a requesting tab, which can then parse the data into its own sessionStorage.
     */
    private dispatchSharedSession(): void {
        // Find sessionStorage entries with "sharedSession" set to true.
        const payload: StorageEventPayload = {};
        try {
            Object.keys(sessionStorage).forEach(key => {
                const value: StorageObject = JSON.parse(sessionStorage.getItem(key) || '{}') as StorageObject;
                if (value && value.sharedSession) {
                    payload[key] = value;
                }
            });
        } catch {}

        // Send payload via "storage" event.
        if (Object.entries(payload).length) {
            localStorage.setItem(this.eventResponseKey, stringify(payload));
            localStorage.removeItem(this.eventResponseKey);
        }
    }

    /**
     * Sets and removes dummy-data with the "clear key" in localStorage to trigger the "storage" event in other tabs.
     * This allows other tabs to clear their shared session as well.
     */
    private dispatchClearSession(): void {
        localStorage.setItem(this.eventClearKey, Date.now().toString());
        localStorage.removeItem(this.eventClearKey);
    }

    /**
     * Sets and removes the passed key as data with the "clear key" in localStorage to trigger the "storage" event in other tabs.
     * This allows other tabs to remove the specific key from their shared session as well.
     * @param storageKey
     */
    private dispatchRemoveKey(storageKey: string): void {
        localStorage.setItem(this.eventRemoveKey, storageKey);
        localStorage.removeItem(this.eventRemoveKey);
    }

    /**
     * Sets up a "storage" event handler to allow communication between tabs.
     * @param e
     */
    private storageEventHandler(e: StorageEvent) {
        if (e.key === this.eventClearKey && sessionStorage.length) {
            // Another tab has cleared shared session data. Remove in this tab as well.
            Object.keys(sessionStorage).forEach(key => {
                sessionStorage.removeItem(key);
                this.externalChanges$.next({ [key]: undefined });
            });
        } else if (e.key === this.eventRemoveKey && e.newValue && sessionStorage.length) {
            // Another tab has removed a shared session key. Remove in this tab as well.
            sessionStorage.removeItem(e.newValue);
            this.externalChanges$.next({ [e.newValue]: undefined });
        } else if (e.key === this.eventRequestKey && e.newValue && sessionStorage.length) {
            // Another tab has asked for the shared session values. Dispatch from this tab.
            this.dispatchSharedSession();
        } else if (e.key === this.eventResponseKey && e.newValue) {
            // Another tab has sent shared session values. Store them for this tab.
            const payload: StorageEventPayload = JSON.parse(e.newValue);
            Object.keys(payload).forEach(key => {
                sessionStorage.setItem(key, stringify(payload[key]));
                this.externalChanges$.next({ [key]: payload[key] });
            });
        } else if (e.key) {
            this.externalChanges$.next({ [e.key]: { newValue: e.newValue, oldValue: e.oldValue } });
        }
    }
}

interface StorageObject {
    /**
     * Stored value - does NOT need to be a string.
     */
    value: any;
    /**
     * Expiration date of value (in "milliseconds since 1970" format).
     */
    timestamp?: number;
    /**
     * Used if sessionStorage needs to publish to other tabs.
     * If you want to be "notified" of the change in the other tab, then you will need to subscribe to getExternalChanges().
     */
    sharedSession?: boolean;
}

interface StorageObjectConfig {
    /**
     * Optionally set number of hours this value should be valid.
     */
    hoursToLive?: number;
    /**
     * Use sessionStorage - Value is only available until current tab is closed or (if sharedSession is true) until window is closed.
     */
    sessionOnly?: boolean;

    /**
     * Use sessionStorage - Allow sessionStorage object to be passed to other tabs.
     */
    sharedSession?: boolean;

    /**
     * Allows fallback to cookie based storage (do NOT use for large objects)
     */
    cookieFallback?: boolean;
    /**
     * Use cookie instead of storage
     */
    useCookie?: boolean;
}

interface StorageEventPayload {
    [key: string]: StorageObject;
}

interface StorageChange {
    [key: string]: any;
}
